/**
 * TaskMasterFilterBar.jsx — Phase 120 — Home dashboard unified filter bar.
 * Phase 144 — Ultra-Wide Command Center: also carries the "View 1/2/3 +
 * Pin Current View" preset controls (right) — see DashboardViewPresets.jsx.
 * Phase 146 — Row 1 of the Command Center: Team/Project/BU filters on the
 * left, View presets + Pin on the right. The Dynamic Date moved out of
 * this row into the TodayBanner hero's white box (far right, Phase 146).
 * Sits inside Dashboard.jsx's sticky header wrapper.
 *
 * Drives BOTH the HomeTaskMetrics stat strip and the TaskHubWidget list
 * below.
 */
import React, { useMemo } from "react";
import { Users2, FolderKanban, Building2 } from "lucide-react";
import DashboardViewPresets from "@/components/DashboardViewPresets";

export default function TaskMasterFilterBar({
  team, projects, currentUserId,
  personId, onPersonChange,
  projectId, onProjectChange,
  businessUnit, onBusinessUnitChange,
  statusFilter, onStatusFilterChange,
}) {
  const businessUnits = useMemo(
    () => [...new Set(projects.map((p) => p.business_unit).filter(Boolean))].sort(),
    [projects],
  );
  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [projects],
  );
  // "Myself" (personId === currentUserId) is the neutral default, not an
  // applied filter — only a different person, a project or a BU counts.
  const anyActive = (personId && personId !== currentUserId) || !!projectId || !!businessUnit;

  return (
    <div data-testid="task-master-filter-bar">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Users2 size={13} className="text-slate-400 shrink-0" />
            <select
              value={personId || ""}
              onChange={(e) => onPersonChange(e.target.value || null)}
              className="h-8 text-xs rounded-sm border border-slate-200 bg-white px-2 max-w-[150px]"
              data-testid="master-filter-person"
            >
              <option value={currentUserId || ""}>Myself</option>
              <option value="">Everyone</option>
              {team.filter((u) => u.id !== currentUserId).map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <FolderKanban size={13} className="text-slate-400 shrink-0" />
            <select
              value={projectId || ""}
              onChange={(e) => onProjectChange(e.target.value || null)}
              className="h-8 text-xs rounded-sm border border-slate-200 bg-white px-2 max-w-[170px]"
              data-testid="master-filter-project"
            >
              <option value="">All projects</option>
              {sortedProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <Building2 size={13} className="text-slate-400 shrink-0" />
            <select
              value={businessUnit || ""}
              onChange={(e) => onBusinessUnitChange(e.target.value || null)}
              className="h-8 text-xs rounded-sm border border-slate-200 bg-white px-2 max-w-[150px]"
              data-testid="master-filter-bu"
            >
              <option value="">All business units</option>
              {businessUnits.map((bu) => <option key={bu} value={bu}>{bu}</option>)}
            </select>
          </div>

          {anyActive && (
            <button
              type="button"
              onClick={() => { onPersonChange(currentUserId || null); onProjectChange(null); onBusinessUnitChange(null); }}
              className="text-[11px] text-[#F54800] hover:underline shrink-0"
              data-testid="master-filter-clear"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Phase 144 — View 1/2/3 presets + Pin Current View, pinned right. */}
        <div className="ml-auto shrink-0">
          <DashboardViewPresets
            currentFilters={{
              person_id: personId || null,
              project_id: projectId || null,
              business_unit: businessUnit || null,
              status_filter: statusFilter || null,
            }}
            onApply={(v) => {
              onPersonChange(v.person_id || null);
              onProjectChange(v.project_id || null);
              onBusinessUnitChange(v.business_unit || null);
              onStatusFilterChange?.(v.status_filter || null);
            }}
          />
        </div>
      </div>
    </div>
  );
}
